import { openDB, IDBPDatabase } from "idb";
import { useEffect, useState, useCallback, useRef } from "react";

interface UseIndexedDBOptions<T> {
    version: number;
    storeName: string;
    keyPath: keyof T;
    onError?: (error: Error) => void;
}

interface UseIndexedDBResult<T> {
    db: IDBPDatabase | null;
    data: T[];
    isLoading: boolean;
    error: Error | null;
    addItem: (item: T) => Promise<void>;
    updateItem: (item: T) => Promise<void>;
    deleteItem: (key: string | number) => Promise<void>;
    getItem: (key: string | number) => Promise<T | null>;
    getAllItems: () => Promise<T[]>;
}

// نگهداری وضعیت پایگاه داده به صورت گلوبال
const DB_NAME = "win11-clone";
let dbInstance: IDBPDatabase | null = null;
let dbInitPromise: Promise<IDBPDatabase> | null = null;

const initializeDB = async (version: number): Promise<IDBPDatabase> => {
    if (!dbInitPromise) {
        dbInitPromise = openDB(DB_NAME, version, {
            upgrade(db, oldVersion, newVersion) {
                const stores = ["logs", "userSettings", "applications", "directories"];
                stores.forEach((store) => {
                    if (!db.objectStoreNames.contains(store)) {
                        db.createObjectStore(store, { keyPath: "id" });
                    }
                });
            },
        });
    }
    return dbInitPromise;
};

export const useIndexedDB = <T>({ version, storeName, keyPath, onError }: UseIndexedDBOptions<T>): UseIndexedDBResult<T> => {
    const [db, setDb] = useState<IDBPDatabase | null>(null);
    const [data, setData] = useState<T[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const initialized = useRef(false);

    const handleError = useCallback(
        (err: Error) => {
            setError(err);
            onError?.(err);
        },
        [onError]
    );

    useEffect(() => {
        let mounted = true;

        const init = async () => {
            if (initialized.current) return;

            try {
                if (!dbInstance) {
                    dbInstance = await initializeDB(version);
                }

                if (!mounted) return;

                setDb(dbInstance);
                const items = await dbInstance.getAll(storeName);

                if (!mounted) return;

                setData(items);
                initialized.current = true;
            } catch (err) {
                if (!mounted) return;
                handleError(err instanceof Error ? err : new Error(`Failed to initialize ${storeName}`));
            } finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };

        init();

        return () => {
            mounted = false;
        };
    }, [version, storeName, handleError]);

    const addItem = useCallback(
        async (item: T) => {
            if (!db) return;
            try {
                // Check if item with same key exists
                const key = item[keyPath as keyof T] as string | number;
                const existingItem = await db.get(storeName, key);

                if (existingItem) {
                    // If item exists, update it instead
                    await db.put(storeName, item);
                } else {
                    // If item doesn't exist, add it
                    await db.add(storeName, item);
                }

                const items = await db.getAll(storeName);
                setData(items);
            } catch (err) {
                handleError(err instanceof Error ? err : new Error("Failed to add item"));
            }
        },
        [db, storeName, handleError, keyPath]
    );

    const updateItem = useCallback(
        async (item: T) => {
            if (!db) return;
            try {
                await db.put(storeName, item);
                const items = await db.getAll(storeName);
                setData(items);
            } catch (err) {
                handleError(err instanceof Error ? err : new Error("Failed to update item"));
            }
        },
        [db, storeName, handleError]
    );

    const deleteItem = useCallback(
        async (key: string | number) => {
            if (!db) return;
            try {
                if (key === "all") {
                    await db.clear(storeName);
                    setData([]);
                } else {
                    await db.delete(storeName, key);
                    const items = await db.getAll(storeName);
                    setData(items);
                }
            } catch (err) {
                handleError(err instanceof Error ? err : new Error("Failed to delete item"));
            }
        },
        [db, storeName, handleError]
    );

    const getItem = useCallback(
        async (key: string | number) => {
            if (!db) return null;
            try {
                return await db.get(storeName, key);
            } catch (err) {
                handleError(err instanceof Error ? err : new Error("Failed to get item"));
                return null;
            }
        },
        [db, storeName, handleError]
    );

    const getAllItems = useCallback(async () => {
        if (!db) return [];
        try {
            return await db.getAll(storeName);
        } catch (err) {
            handleError(err instanceof Error ? err : new Error("Failed to get all items"));
            return [];
        }
    }, [db, storeName, handleError]);

    return {
        db,
        data,
        isLoading,
        error,
        addItem,
        updateItem,
        deleteItem,
        getItem,
        getAllItems,
    };
};
